import { BaseComponent } from '../../base-component';
import { ProductComparisonApi } from '../../product-comparison-api/client/product-comparison';
import { getCurrentMajorBreakpoint, MajorBreakpoint } from '../../_common/client/breakpoint';
import { submitMerge, submitEvent, submitMove } from '@otto-ec/tracking-bct';
import { TrackingStatus } from '../../tracking/client/event-data-types';
import { push as showSnackbar } from '@otto-ec/otto-components/snackbar/v1';

enum ScrollDirection {
    Left,
    Right,
}

/*                                                                                                                         */
export class ProductComparisonContentComponent extends BaseComponent {
    public rootSelector = '.js_pcp_content';
    private productComparisonApi: ProductComparisonApi | undefined;
    private isExternalCall = false;
    private currentMajorBreakpoint = MajorBreakpoint.Mobile;
    private scrollButtonsActive =
        document.querySelectorAll('.js_pcp_content__scroll-button--left, .js_pcp_content__scroll-button--right').length === 2;

    public async init(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }

        this.isExternalCall = this.getRootElement().dataset.isExternalCall === 'true';
        this.currentMajorBreakpoint = getCurrentMajorBreakpoint();
        this.initializeProductComparisonApi();
        this.initializeDiscoverMoreText();

        if (this.scrollButtonsActive) {
            this.positionScrollButtons();
            this.refreshScrollButtonsVisibility();
        }
        this.addEventListeners();
        this.registerClickOnAttributeGroupAccordion();
        this.registerSwitchToggleEventListener();

        submitMerge({
            ot_PageCluster: ['Produktvergleich'],
        });
        this.trackLoadedVariations();
        this.trackErrorMessage();
    }

    public async onHtmlLoaded(): Promise<void> {
        /*                                  */
        return;
    }

    private initializeProductComparisonApi(): void {
        o_global.eventQBus.on('ft5.productComparison.loaded', api => {
            this.productComparisonApi = api;
        });
    }

    private initializeDiscoverMoreText(): void {
        const breadcrumbLinkElements = this.getRootElement().querySelectorAll<HTMLLinkElement>('.js_pcp_content__breadcrumb ul li a');

        const breadcrumbLinkElementsLength = breadcrumbLinkElements.length;
        if (breadcrumbLinkElementsLength < 2) {
            return;
        }

        const { href, textContent } = breadcrumbLinkElements[breadcrumbLinkElementsLength - 1];
        if (!href || !textContent) {
            return;
        }

        const discoverMoreLinkElement = document.createElement('oc-link-v2');
        discoverMoreLinkElement.base64Href = window.btoa(href);
        discoverMoreLinkElement.textContent = textContent.replace('alle ', '');
        discoverMoreLinkElement.addEventListener('click', () => {
            submitEvent({
                product_ComparisonLink: [textContent!],
                product_ComparisonAmount: [`${this.getVariationCount()}`],
            });
        });

        const discoverMoreTextElement = this.getChildElement('.js_pcp_content__discover-more-text');
        discoverMoreTextElement.append('Oder entdecke weitere ');
        discoverMoreTextElement.append(discoverMoreLinkElement);
        discoverMoreTextElement.append('.');

        discoverMoreTextElement.classList.remove('pl_hidden');
    }

    private positionScrollButtons(): void {
        const windowInnerHeight = window.innerHeight;
        const tableViewScrollerElement = this.getChildElement('.js_pcp_content__table-view-scroller');
        const tableViewScrollerElementTop = tableViewScrollerElement.getBoundingClientRect().top;
        const visibleHeightOfTableViewScrollerElement =
            tableViewScrollerElementTop > 0 ? windowInnerHeight - tableViewScrollerElementTop : windowInnerHeight;
        const halfButtonHeight = 24;
        const buttonBottomPosition = visibleHeightOfTableViewScrollerElement / 2 - halfButtonHeight;
        this.getChildElement('.js_pcp_content__scroll-button--left').style.bottom = `${buttonBottomPosition}px`;
        this.getChildElement('.js_pcp_content__scroll-button--right').style.bottom = `${buttonBottomPosition}px`;
    }

    private refreshScrollButtonsVisibility(): void {
        const tableViewScrollerElement = this.getChildElement('.js_pcp_content__table-view-scroller');
        const tableViewElement = this.getChildElement('.js_pcp_content__table-view');
        const maxScrollLeft = tableViewElement.offsetWidth - tableViewScrollerElement.offsetWidth;
        const scrollButtonLeftElement = this.getChildElement<HTMLButtonElement>('.js_pcp_content__scroll-button--left');
        const scrollButtonRightElement = this.getChildElement<HTMLButtonElement>('.js_pcp_content__scroll-button--right');
        if (tableViewScrollerElement.scrollLeft === 0) {
            scrollButtonLeftElement.classList.add('pl_hidden');
        } else {
            scrollButtonLeftElement.classList.remove('pl_hidden');
        }
        if (maxScrollLeft <= 0 || tableViewScrollerElement.scrollLeft === maxScrollLeft) {
            scrollButtonRightElement.classList.add('pl_hidden');
        } else {
            scrollButtonRightElement.classList.remove('pl_hidden');
        }
    }

    private handleScrollButtonClick(direction: ScrollDirection) {
        const tableViewScrollerElement = this.getChildElement('.js_pcp_content__table-view-scroller');
        const columnWidth = this.getChildElement('.js_pcp_content__column-list-item').offsetWidth;
        const gapBetweenItems = 16;
        const scrollWidth = columnWidth + gapBetweenItems;
        if (direction === ScrollDirection.Left) {
            /*                                                                           */
            tableViewScrollerElement.scrollLeft = tableViewScrollerElement.scrollLeft - scrollWidth;
        } else {
            /*                                                                                                */
            /*                              */
            tableViewScrollerElement.scrollLeft = tableViewScrollerElement.scrollLeft + scrollWidth;
        }
    }

    private addEventListeners(): void {
        const tableViewScrollerElement = this.getChildElement('.js_pcp_content__table-view-scroller');
        const imageBarElement = this.getChildElement('.js_pcp_content__image-bar');

        window.addEventListener('resize', () => {
            if (this.scrollButtonsActive) {
                this.refreshScrollButtonsVisibility();
            }
            this.currentMajorBreakpoint = getCurrentMajorBreakpoint();
        });

        window.addEventListener('scroll', () => {
            this.scaleUnscaleImages(tableViewScrollerElement);
        });

        if (this.scrollButtonsActive) {
            this.getChildElement('.js_pcp_content__scroll-button--left').addEventListener('click', () => {
                this.handleScrollButtonClick(ScrollDirection.Left);
            });

            this.getChildElement('.js_pcp_content__scroll-button--right').addEventListener('click', () => {
                this.handleScrollButtonClick(ScrollDirection.Right);
            });
        }

        tableViewScrollerElement.addEventListener('scroll', () => {
            if (this.scrollButtonsActive) {
                this.refreshScrollButtonsVisibility();
            }
            imageBarElement.scrollLeft = tableViewScrollerElement.scrollLeft;
        });

        this.getChildElements('.js_pcp_content__variation-delete-button').forEach(buttonElement => {
            buttonElement.addEventListener('click', () => this.handleDeleteEvent(buttonElement));
        });

        this.getChildElements('.js_pcp_content__back-button').forEach(buttonElement => {
            buttonElement.addEventListener('click', () => ProductComparisonContentComponent.trackBackNavigationClickEvent());
        });

        this.getChildElements('.js_pcp_content__variation-link').forEach((buttonElement: HTMLElement) => {
            buttonElement.addEventListener('click', () => ProductComparisonContentComponent.trackVariationLinkClickEvent(buttonElement));
        });
    }

    private registerClickOnAttributeGroupAccordion(): void {
        o_global.eventQBus.on('assets.accordion.toggled', event => {
            if (event.currentTarget.dataset.name === 'product_comparison_attribute_group_accordion') {
                ProductComparisonContentComponent.trackClickOnAttributeGroupAccordion(event);
            }
        });
    }

    private handleDeleteEvent(buttonElement: HTMLElement): boolean {
        const variationId = buttonElement.dataset.variationId ?? '';
        const index = buttonElement.dataset.index ?? '';
        this.getChildElements(`[data-variation-id="${variationId}"]`).forEach(element => element.remove());

        const contextId: string = this.getRootElement().dataset.contextId ?? '';
        this.productComparisonApi?.removeFromList(contextId, variationId);

        showSnackbar('Der Artikel wurde von deinem Vergleich gelöscht');

        const titleElement = this.getChildElement('.js_pcp_content-title');
        const variationCount = this.getVariationCount();
        titleElement.textContent = `Produktvergleich (${variationCount} Produkt${variationCount !== 1 ? 'e' : ''})`;
        ProductComparisonContentComponent.trackDeleteVariation(parseInt(index), variationId);

        if (this.scrollButtonsActive) {
            this.refreshScrollButtonsVisibility();
        }

        return false;
    }

    private getVariationCount(): number {
        return this.getChildElements('.js_pcp_content__image-bar .js_pcp_content__variation-delete-button').length;
    }

    private registerSwitchToggleEventListener(): void {
        o_global.eventQBus.on('assets.switch.changed', event => {
            if (event.target.name === 'product_comparison_differences_switch') {
                const tableElement = this.getChildElement('.js_pcp_content__table-view');
                if (event.target.checked) {
                    tableElement.classList.add('pcp_content__table-view--hide-same-content');

                    showSnackbar('Nur Unterschiede anzeigen aktiviert');
                    ProductComparisonContentComponent.trackShowDifferences(true);
                } else {
                    tableElement.classList.remove('pcp_content__table-view--hide-same-content');

                    showSnackbar('Nur Unterschiede anzeigen deaktiviert');
                    ProductComparisonContentComponent.trackShowDifferences(false);
                }
            }
        });
    }

    private getStableStickyContentHeight(): number {
        if (!this.isExternalCall && this.currentMajorBreakpoint === MajorBreakpoint.Desktop) {
            return 80 + 40;
        }
        return 40;
    }

    private scaleUnscaleImages(tableViewScrollerElement: HTMLElement): void {
        const stickyVariationImagesElement = this.getChildElement('.js_pcp_content__image-bar');
        const position = tableViewScrollerElement.getBoundingClientRect();

        if (position.top <= -120 + this.getStableStickyContentHeight()) {
            stickyVariationImagesElement.classList.add('pcp_content__image-bar--visible');
        } else {
            stickyVariationImagesElement.classList.remove('pcp_content__image-bar--visible');
        }
    }

    public getApi(): ProductComparisonApi | undefined {
        return this.productComparisonApi;
    }

    /*       */
    private trackLoadedVariations(): void {
        const tableViewScrollerElement = this.getChildElement('.js_pcp_content__table-view-scroller');
        const tableViewScrollerElementWidth = tableViewScrollerElement.getBoundingClientRect().width;
        const features = this.getChildElements('.js_pcp_content__column-list-item').map((itemElement, index) => ({
            id: `ft5_productcomparison_p${index + 1}`,
            parentId: 'ft5_productcomparison',
            name: 'Produktvergleich-Produkt',
            position: index + 1,
            status: itemElement.getBoundingClientRect().left < tableViewScrollerElementWidth ? 'visible' : ('hidden' as TrackingStatus),
            variationId: itemElement.dataset.variationId,
        }));

        submitMerge({}, [
            {
                id: 'ft5_productcomparison',
                name: 'Produktvergleich',
                status: 'loaded',
            },
            ...features,
        ]);
    }

    private trackErrorMessage(): void {
        const messages = this.getRootElement().querySelector('.js_pcp_content__messages');
        if (messages) {
            submitMerge({}, [
                {
                    id: 'ft5_productcomparison',
                    name: 'Produktvergleich',
                    status: 'error',
                },
            ]);
        }
    }

    private static trackDeleteVariation(index: number, variationId: string): void {
        submitEvent(
            {
                ot_PageCluster: ['Produktvergleich'],
            },
            {
                name: 'click',
                features: [
                    {
                        id: 'ft5_productcomparison',
                        name: 'Produktvergleich',
                        status: 'clicked',
                        labels: {
                            product_ProductComparisonActivity: ['delete'],
                        },
                    },
                    {
                        id: `ft5_productcomparison_p${index + 1}`,
                        parentId: 'ft5_productcomparison',
                        name: 'Produktvergleich-Produkt',
                        position: index + 1,
                        status: 'deleted',
                        variationId: variationId,
                    },
                ],
            }
        );
    }

    private static trackShowDifferences(isOn: boolean): void {
        const switchState = isOn ? 'differences_on' : 'differences_off';
        submitEvent(
            {
                ot_PageCluster: ['Produktvergleich'],
            },
            {
                name: 'click',
                features: [
                    {
                        id: 'ft5_productcomparison',
                        name: 'Produktvergleich',
                        status: 'clicked',
                        labels: {
                            product_ProductComparisonActivity: [switchState],
                        },
                    },
                ],
            }
        );
    }

    private static trackVariationLinkClickEvent(button: HTMLElement): void {
        const variationId: string = button.dataset.variationId ?? '';
        const index: string = button.dataset.index ?? '';
        const navigation: string = button.dataset.navigation ?? '';
        submitMove(
            {
                ot_PageCluster: ['Produktvergleich'],
            },
            {
                name: 'click',
                features: [
                    {
                        id: 'ft5_productcomparison',
                        name: 'Produktvergleich',
                        status: 'clicked',
                    },
                    {
                        id: index,
                        parentId: 'ft5_productcomparison',
                        name: 'Produktvergleich-Produkt',
                        position: parseInt(index) + 1,
                        status: 'clicked',
                        variationId,
                        labels: {
                            product_ProductComparisonNavigation: [navigation],
                        },
                    },
                ],
            }
        );
    }

    private static trackBackNavigationClickEvent(): void {
        submitEvent({
            product_ProductComparisonActivity: ['close'],
        });
    }

    private static trackClickOnAttributeGroupAccordion(event: Event): void {
        const currentTargetElement = event.currentTarget as HTMLElement;
        const attributeGroupName: string = currentTargetElement.dataset.attributeGroupName ?? '';
        const isOpen: boolean = currentTargetElement.classList?.contains('pl_accordion__header--open');
        const activity = isOpen ? 'unfold' : 'fold';

        submitEvent({
            product_ProductComparisonActivity: [activity],
            product_ProductComparisonAttributeGroup: [attributeGroupName],
        });
    }
}
